import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type TextareaHTMLAttributes,
} from "react";

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "defaultValue" | "onChange"> & {
  /** Valeur au montage. Pour repartir d'une autre valeur, changer la `key` du composant. */
  initialValue: string;
  onValueChange?: (value: string) => void;
};

/**
 * Zone de texte compatible avec les outils de dictée (Wispr Flow…).
 *
 * Ces outils insèrent souvent le texte directement dans le champ, sans événement « input ».
 * Un champ contrôlé par React revient alors à l'ancien texte au rendu suivant, et l'état React
 * ne voit jamais le texte dicté. Ici le champ n'est jamais réécrit par React : on relit sa valeur
 * réelle à chaque saisie, à la sortie du champ et toutes les 400 ms tant qu'il est monté.
 * Le parent qui enregistre doit lire `ref.current.value` au moment d'enregistrer.
 *
 * `defaultValue` reste figé au montage : si le parent renvoie le texte courant en `initialValue`,
 * React réécrirait le contenu DOM du champ après chaque dictée, et Wispr Flow perdrait la suivante.
 */
export const DictationTextarea = forwardRef<HTMLTextAreaElement, Props>(function DictationTextarea(
  { initialValue, onValueChange, onBlur, ...rest },
  forwarded,
) {
  const inner = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(forwarded, () => inner.current as HTMLTextAreaElement);
  const mountValue = useRef(initialValue).current;
  const last = useRef(initialValue);
  const callback = useRef(onValueChange);
  callback.current = onValueChange;

  const report = useCallback(() => {
    const value = inner.current?.value ?? "";
    if (value === last.current) return;
    last.current = value;
    callback.current?.(value);
  }, []);

  useEffect(() => {
    const id = window.setInterval(report, 400);
    return () => window.clearInterval(id);
  }, [report]);

  return (
    <textarea
      {...rest}
      ref={inner}
      defaultValue={mountValue}
      onInput={report}
      onBlur={(e) => {
        report();
        onBlur?.(e);
      }}
    />
  );
});
